// Content language helpers for editorial content (Insights, author, links).
// The UI supports PL / EN / ZH; content falls back to EN when a ZH copy
// has not been written yet.

export type ContentLang = "pl" | "en" | "zh";

/** Every localized value must ship all three languages - no EN/PL fallbacks. */
export type Localized<T> = { pl: T; en: T; zh: T };

/** Loose shape so `as const` content objects (with literal types) still fit. */
type AnyLocalized<T> = { pl: T; en: unknown; zh: unknown };

/** Map an i18next language tag to one of the content languages. */
export const contentLang = (lang?: string): ContentLang => {
  const l = (lang || "en").toLowerCase();
  if (l.startsWith("pl")) return "pl";
  if (l.startsWith("zh")) return "zh";
  return "en";
};

/** Pick the right locale from a localized object. */
export function pick<T>(value: AnyLocalized<T>, lang: ContentLang): T {
  return value[lang] as T;
}


/** BCP-47 tag used in metadata / schema.org. */
export const bcp47 = (lang: ContentLang) =>
  lang === "pl" ? "pl-PL" : lang === "zh" ? "zh-CN" : "en";

/** OG locale tag. */
export const ogLocale = (lang: ContentLang) =>
  lang === "pl" ? "pl_PL" : lang === "zh" ? "zh_CN" : "en_US";

/** Small UI strings used inside editorial components. */
const STRINGS = {
  aboutAuthor: { pl: "O autorze", en: "About the author", zh: "关于作者" },
  authorProfile: { pl: "Profil autora", en: "Author profile", zh: "作者主页" },
  writtenBy: { pl: "Autor", en: "Written by", zh: "作者" },
  keyTakeaways: { pl: "Najważniejsze wnioski", en: "Key takeaways", zh: "核心要点" },
  relatedGuides: { pl: "Powiązane przewodniki", en: "Related guides", zh: "相关指南" },
  keepReading: { pl: "Czytaj dalej", en: "Keep reading", zh: "继续阅读" },
  allInsights: { pl: "← Wszystkie insights", en: "← All insights", zh: "← 全部洞察" },
  home: { pl: "Start", en: "Home", zh: "首页" },
  homePage: { pl: "Strona główna", en: "Home", zh: "首页" },
  about: { pl: "O nas", en: "About", zh: "关于我们" },
  expert: { pl: "Ekspert · Rekrutacja IT", en: "Expert · IT recruitment", zh: "专家 · IT 招聘" },
  experience: { pl: "Doświadczenie", en: "Experience", zh: "从业经历" },
  expertise: { pl: "Specjalizacje", en: "Areas of expertise", zh: "专业领域" },
  publications: { pl: "Publikacje i przewodniki", en: "Guides and publications", zh: "指南与文章" },
  talkTitle: {
    pl: "Porozmawiajmy o Twojej rekrutacji",
    en: "Let's talk about your hiring",
    zh: "聊聊你的招聘需求",
  },
  marketRead: { pl: "Poproś o odczyt rynku", en: "Request a market read", zh: "获取市场行情解读" },
  marketData: { pl: "Dane rynkowe", en: "Market data", zh: "市场数据" },
  lastUpdated: { pl: "Ostatnia aktualizacja", en: "Last updated", zh: "最近更新" },
} satisfies Record<string, Localized<string>>;

export type UiKey = keyof typeof STRINGS;

export const tt = (key: UiKey, lang: ContentLang) => pick(STRINGS[key], lang);

/**
 * Self-referencing URL for a language version.
 * PL is the default version and keeps the clean URL (no ?lang= parameter),
 * EN and ZH are addressed with ?lang=.
 */
export const langUrl = (base: string, lang: ContentLang) =>
  lang === "pl" ? base : `${base}${base.includes("?") ? "&" : "?"}lang=${lang}`;
